// @ts-check

import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import { Logo } from '@artprov/design-system'
import { IconButton, Dropdown, TextButton } from '@artprov/design-system'
import { SubMenu } from '@artprov/design-system'
import { cn } from '@artprov/design-system'

/**
 * Opciones para el menú del perfil.
 * @typedef {Object} OpcionMenu
 * @property {string} icon - Nombre del icono de la opción (por ejemplo, 'person', 'settings').
 * @property {string} opcion - Texto que se mostrará en la opción del menú.
 */

/**
 * Propiedades para el componente Header.
 * @typedef {Object} HeaderProps
 * @property {OpcionMenu[]} opcionesDelMenu=[] - Array de opciones del menú para el perfil.
 * @property {string[]} [submenuItems] - Array de items del submenú.
 * @property {string} [logoHref] - Href del logo.
 * @property {boolean} [alertActive=false] - Indica si la alerta de notificaciones está activa.
 * @property {string} [className=''] - Clase adicional para el componente Header.
 * @property {boolean} [showDropdown=true] - Muestra/oculta el bloque de perfil.
 * @property {boolean} [showNotifications=true] - Muestra/oculta el icono de notificaciones.
 * @property {string} [classNameContainerFlex] - Clase adicional para el contenedor flex del submenú.
 * @property {boolean} [showHelpDesk=false] - Muestra/oculta el bloque el icono de help.
 * @property {function} [onHelpClick] - Función a ejecutar al hacer click en el icono de ayuda.
 */

/**
 * Componente Header similar al diseño en la imagen proporcionada.
 * Incluye el logo, un botón de notificaciones y un menú desplegable de perfil.
 *
 * @param {HeaderProps} props - Propiedades del componente.
 * @returns {JSX.Element} El componente de encabezado.
 */
export const HeaderOs = ({
  opcionesDelMenu,
  submenuItems = [],
  alertActive = false,
  className = '',
  logoHref = '/',
  showDropdown = true,
  showNotifications = true,
  classNameContainerFlex,
  showHelpDesk = false,
  onHelpClick,
}) => {
  const [isTabletOrSmaller, setIsTabletOrSmaller] = useState(false)

  useEffect(() => {
    const mediaQuery = globalThis.matchMedia('(max-width: 1024px)')

    const handleMediaQueryChange = (event) => {
      setIsTabletOrSmaller(event.matches)
    }

    mediaQuery.addListener(handleMediaQueryChange)

    // Comprobamos el tamaño de la pantalla al cargar sin llamar setState sincrónicamente en el efecto
    requestAnimationFrame(() => {
      setIsTabletOrSmaller(mediaQuery.matches)
    })

    return () => {
      mediaQuery.removeListener(handleMediaQueryChange)
    }
  }, [])

  return (
    <header className={cn('bg-tertiary-98 flex flex-col items-center m-auto', className)}>
      <div className="w-full flex justify-between items-center h-[72px] px-10 lg:px-24">
        <div className="flex items-center">
          <a href={logoHref}>
            <TextButton className="hover:none hover:bg-transparent p-0" classNameDescription="p-0" classNameIcon="p-0">
              <Logo className="h-8" variant={isTabletOrSmaller ? 'Iso positivo' : 'Logo positivo'} />
            </TextButton>
          </a>
        </div>
        <div className="flex items-center">
          {!isTabletOrSmaller && showHelpDesk && (
            <IconButton icon="help" size="medium" className="text-gray-500" onClick={onHelpClick} />
          )}
          {!isTabletOrSmaller && showNotifications && (
            <IconButton icon="notifications" alertActive={alertActive} size="medium" className="text-gray-500" />
          )}
          {showDropdown && (
            <Dropdown
              textButton="Mi perfil"
              opcionesDelMenu={opcionesDelMenu}
              mobile={isTabletOrSmaller}
              className="ml-auto"
            />
          )}
        </div>
      </div>
      {!isTabletOrSmaller && submenuItems?.length > 0 && (
        <div className="flex w-full top-1">
          <SubMenu
            submenuItems={submenuItems}
            classNameContainerMenu="w-full"
            classNameContainerButton="mx-0 xl:px-8"
            classNameContainerFlex={classNameContainerFlex}
          />
        </div>
      )}
    </header>
  )
}

HeaderOs.propTypes = {
  opcionesDelMenu: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string,
      opcion: PropTypes.string,
    })
  ),
  submenuItems: PropTypes.arrayOf(PropTypes.string),
  alertActive: PropTypes.bool,
  className: PropTypes.string,
  logoHref: PropTypes.string,
  showDropdown: PropTypes.bool,
  showNotifications: PropTypes.bool,
  classNameContainerFlex: PropTypes.string,
  showHelpDesk: PropTypes.bool,
  onHelpClick: PropTypes.func,
}
